import React, { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";
import { AlertError, AlertSuccess } from "./components/Alert";
import Button from "./components/Button";
import Loading from "./components/Loading";
import { useAuth } from "./authContext";
import { useNavigate } from "react-router-dom";

const API =
  process.env.NODE_ENV === "development" ? "http://localhost:8080/api" : "/api";

interface CompanyItem {
  id: number;
  company_name: string;
  company_address: string;
  company_tel: string;
  role: string;
  is_default: boolean;
  package: string;
  due_expired: string;
}

interface PendingInvite {
  id: number;
  company_id: number;
  company_name: string;
  role_name: string;
  expires_at: string;
}

function Onboarding() {
  const { session, signOut, refreshProfile } = useAuth();
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<CompanyItem[]>([]);
  const [pendingInvites, setPendingInvites] = useState<PendingInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [accepting, setAccepting] = useState<number | null>(null);

  const [companyName, setCompanyName] = useState("");
  const [companyAddress, setCompanyAddress] = useState("");
  const [companyTel, setCompanyTel] = useState("");
  const [juristicNumber, setJuristicNumber] = useState("");
  const [companyType, setCompanyType] = useState("นิติบุคคล");
  const [isVat, setIsVat] = useState(false);
  const [contactName, setContactName] = useState("");
  const [pinMaster, setPinMaster] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  async function loadData() {
    setLoading(true);
    await Promise.all([loadCompanies(), loadPendingInvites()]);
    setLoading(false);
  }

  async function loadCompanies() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(API + "/v1/companies/me", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (data.result === "success") {
        setCompanies(data.companies || []);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function loadPendingInvites() {
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(API + "/v1/invitations/pending", {
        headers: { Authorization: `Bearer ${session?.access_token}` },
      });
      const data = await res.json();
      if (data.result === "success") {
        setPendingInvites(data.invitations || []);
      }
    } catch (err) {
      console.error(err);
    }
  }

  async function acceptInvitation(invitationId: number) {
    setAccepting(invitationId);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(API + "/v1/invitations/accept", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ invitation_id: invitationId }),
      });
      const data = await res.json();
      if (data.result === "success") {
        AlertSuccess("ตอบรับคำเชิญสำเร็จ!");
        await refreshProfile();
        navigate("/home");
      } else {
        AlertError(data.message || "เกิดข้อผิดพลาด");
      }
    } catch {
      AlertError("เกิดข้อผิดพลาดในการเชื่อมต่อ");
    }
    setAccepting(null);
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault();
    if (!companyName.trim()) return;

    setSaving(true);
    try {
      const {
        data: { session },
      } = await supabase.auth.getSession();
      const res = await fetch(API + "/v1/companies", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({
          company_name: companyName.trim(),
          company_address: companyAddress.trim(),
          company_tel: companyTel.trim(),
          juristic_number: juristicNumber.trim(),
          company_type: companyType,
          is_vat: isVat,
          contact_name: contactName.trim(),
          pin_master: pinMaster.trim(),
        }),
      });
      const data = await res.json();
      if (data.result === "success") {
        AlertSuccess("สร้างธุรกิจสำเร็จ");
        window.location.href = "/home";
      } else {
        AlertError(data.message || "เกิดข้อผิดพลาด");
      }
    } catch (err) {
      AlertError("ไม่สามารถสร้างธุรกิจได้");
    }
    setSaving(false);
  }

  async function switchCompany(companyId: number) {
    setSaving(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const res = await fetch(API + "/v1/companies/switch", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${session?.access_token}`,
        },
        body: JSON.stringify({ company_id: companyId }),
      });
      const data = await res.json();
      if (data.result === "success") {
        await refreshProfile();
        navigate("/home");
      } else {
        AlertError(data.message || "เกิดข้อผิดพลาด");
      }
    } catch {
      AlertError("เกิดข้อผิดพลาดในการเชื่อมต่อ");
    }
    setSaving(false);
  }

  function resetForm() {
    setCompanyName("");
    setCompanyAddress("");
    setCompanyTel("");
    setJuristicNumber("");
    setCompanyType("นิติบุคคล");
    setIsVat(false);
    setContactName("");
    setPinMaster("");
    setShowForm(false);
  }

  if (loading) return <Loading isLoading={true} />;

  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center px-4 py-8">
      <Loading isLoading={saving} />
      <div className="w-full max-w-2xl">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 sm:p-8">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div>
              <small className="text-sm text-gray-500">เข้าสู่ระบบด้วย</small>
              <div className="font-semibold text-gray-900">{session?.user?.email}</div>
            </div>
            <Button
              variant="neutral"
              size="md"
              onClick={signOut}
            >
              ออกจากระบบ
            </Button>
          </div>
          <hr className="border-gray-200 mb-4" />

          {/* Pending Invitations */}
          {pendingInvites.length > 0 && (
            <div className="mb-4">
              <h6 className="text-center text-sm font-semibold text-gray-700 mb-3">คำเชิญที่รอตอบรับ</h6>
              <div className="space-y-2">
                {pendingInvites.map((inv) => (
                  <div
                    key={inv.id}
                    className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3"
                  >
                    <div className="min-w-0">
                      <span className="font-semibold text-gray-900">{inv.company_name}</span>
                      <br />
                      <small className="text-gray-500">
                        ตำแหน่ง: {inv.role_name} | หมดอายุ: {inv.expires_at}
                      </small>
                    </div>
                    <Button
                      variant="success"
                      size="sm"
                      onClick={() => acceptInvitation(inv.id)}
                      disabled={accepting !== null}
                    >
                      {accepting === inv.id ? "กำลังตอบรับ..." : "ยอมรับ"}
                    </Button>
                  </div>
                ))}
              </div>
              <hr className="border-gray-200 my-4" />
            </div>
          )}

          <h5 className="text-center text-lg font-semibold text-gray-900 mb-4">รายการธุรกิจ</h5>

          {/* Company List */}
          {companies.length > 0 && (
            <div className="space-y-2 mb-4">
              {companies.map((c) => (
                <div
                  key={c.id}
                  className="flex items-center justify-between border border-gray-200 rounded-lg px-4 py-3 hover:bg-gray-50 cursor-pointer"
                >
                  <div className="min-w-0">
                    <span className="font-semibold text-gray-900">{c.company_name}</span>
                    {c.company_address && (
                      <>
                        <br />
                        <small className="text-gray-500">{c.company_address}</small>
                      </>
                    )}
                  </div>
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={() => switchCompany(c.id)}
                    disabled={saving}
                  >
                    เข้าใช้งาน
                  </Button>
                </div>
              ))}
            </div>
          )}

          {companies.length === 0 && !showForm && pendingInvites.length === 0 && (
            <p className="text-sm text-gray-500 text-center mb-3">
              ยังไม่มีธุรกิจ กรุณาสร้างธุรกิจใหม่
            </p>
          )}

          {!showForm && (
            <Button
              variant="success"
              size="full"
              onClick={() => setShowForm(true)}
            >
              สร้างเต็นท์รถ
            </Button>
          )}

          {/* Create Form */}
          {showForm && (
            <form onSubmit={handleCreate} className="mt-4">
              <hr className="border-gray-200 mb-4" />
              <h6 className="text-sm font-semibold text-gray-700 mb-3">สร้างธุรกิจใหม่</h6>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อธุรกิจ/เต็นท์รถ *</label>
                <input
                  type="text"
                  value={companyName}
                  onChange={(e) => setCompanyName(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">ที่อยู่</label>
                <input
                  type="text"
                  value={companyAddress}
                  onChange={(e) => setCompanyAddress(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">เบอร์โทร</label>
                  <input
                    type="text"
                    value={companyTel}
                    onChange={(e) => setCompanyTel(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">เลขนิติบุคคล/เลขประจำตัวผู้เสียภาษี</label>
                  <input
                    type="text"
                    value={juristicNumber}
                    onChange={(e) => setJuristicNumber(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ประเภท</label>
                  <select
                    value={companyType}
                    onChange={(e) => setCompanyType(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="นิติบุคคล">นิติบุคคล</option>
                    <option value="บุคคลธรรมดา">บุคคลธรรมดา</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">ชื่อผู้ติดต่อ</label>
                  <input
                    type="text"
                    value={contactName}
                    onChange={(e) => setContactName(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>

              <div className="flex items-center gap-2 mb-4">
                <input
                  type="checkbox"
                  id="isVat"
                  checked={isVat}
                  onChange={(e) => setIsVat(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <label htmlFor="isVat" className="text-sm text-gray-700">จด VAT</label>
              </div>

              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">PIN CODE *</label>
                <input
                  type="password"
                  maxLength={10}
                  value={pinMaster}
                  onChange={(e) => setPinMaster(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
                <small className="text-gray-400">รหัสยืนยันตัวตนสำหรับแก้ไขข้อมูล</small>
              </div>

              <div className="flex gap-2">
                <Button
                  type="submit"
                  variant="primary"
                  size="lg"
                >
                  สร้างธุรกิจ
                </Button>
                <Button
                  type="button"
                  variant="neutral"
                  size="lg"
                  onClick={resetForm}
                >
                  ยกเลิก
                </Button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}

export default Onboarding;
